// PDF viewer modal — full-screen-ish preview for generated quote PDFs and
// saved drawing attachments. Lazy-loads pdfjs-dist on first open so the
// main bundle stays lean (same pattern as `src/shared/quotePdf.ts` and the
// jsPDF dynamic import there).
//
// Sources:
//   - Blob   → a freshly generated PDF (e.g. straight out of generateQuotePdf)
//   - string → an http(s)/data URL pointing at a saved attachment
//   - null   → "no PDF loaded" empty state (so the modal can mount before
//              the caller has the source ready)
//
// Keyboard contract (mirrors NewQuoteModal/NewPartModal Esc-to-close):
//   - Esc          → close
//   - Left / Right → prev / next page
//   - + / =        → zoom in   (= is the unshifted '+' on most US layouts)
//   - - / _        → zoom out
//   - 0            → reset zoom to 100%
//
// Worker setup — pdfjs-dist needs its worker URL set BEFORE getDocument is
// called. We use Vite's `new URL(..., import.meta.url)` pattern with the
// .mjs build, which Vite's dep optimizer rewrites to a hashed asset URL
// at build time and serves natively in dev. No copy-to-public step needed.
// (If a future change breaks this, fall back to copying pdf.worker.min.mjs
// into /public/ and pointing GlobalWorkerOptions.workerSrc there.)
//
// Cleanup: blob URLs created from a Blob source are revoked on
// unmount/source-change; the PDFDocumentProxy is destroyed; in-flight
// render tasks are cancelled so a fast page-flip doesn't paint stale
// pixels over the new page.

import { useEffect, useRef, useState } from 'react';
import { ref as storageRef, getBlob } from 'firebase/storage';
import { storage } from '@/shared/firebase';

export interface PdfViewerModalProps {
  open: boolean;
  onClose: () => void;
  // EITHER a Blob (just-generated PDF) OR a URL (saved attachment).
  source: Blob | string | null;
  // Optional title shown in the header bar (e.g. "Quote Q-1042.pdf").
  title?: string;
  // Optional filename for the Download button. If omitted, "document.pdf".
  downloadFilename?: string;
}

// Lazy-loaded subset of pdfjs-dist types — we only touch what we need.
// Using `unknown` for transport-level fields keeps us away from internal
// shapes that churn between minor versions.
interface PdfDocLike {
  numPages: number;
  getPage(n: number): Promise<PdfPageLike>;
  destroy(): Promise<void>;
}
interface PdfPageLike {
  getViewport(opts: { scale: number }): { width: number; height: number };
  render(opts: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }): { promise: Promise<void>; cancel: () => void };
  cleanup?: () => void;
}

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;
const DEFAULT_ZOOM_INDEX = 2; // → 1.0

export function PdfViewerModal({
  open,
  onClose,
  source,
  title,
  downloadFilename,
}: PdfViewerModalProps) {
  // ───── pdf doc state ─────
  const [doc, setDoc] = useState<PdfDocLike | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [zoomIndex, setZoomIndex] = useState<number>(DEFAULT_ZOOM_INDEX);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Stable blob URL for the download button + (when source is a Blob) the
  // pdfjs loader. Kept in a ref so we can revoke it exactly once on
  // unmount or source change.
  const blobUrlRef = useRef<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Track the most recent in-flight render so a fast page-flip can cancel
  // the stale one before it paints.
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);

  // ────────────────────────────────────────────────────────────
  // Load / reload the document whenever the modal opens or source changes.
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || source == null) {
      setDoc(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPageNum(1);
    setZoomIndex(DEFAULT_ZOOM_INDEX);

    // For Blob sources we keep an object URL alive so the Download button
    // has something to hand the browser. For string sources, the URL itself
    // is the download target.
    if (typeof source !== 'string') {
      const url = URL.createObjectURL(source);
      blobUrlRef.current = url;
    }

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');

        // One-time worker URL setup. Setting it more than once is harmless
        // (the second assignment is a no-op against the same string), but
        // we still guard so we don't allocate a new URL object per open.
        const opts = pdfjs.GlobalWorkerOptions as { workerSrc: string };
        if (!opts.workerSrc) {
          // Vite-compatible worker URL — the .mjs build matches the main
          // pdf.mjs ESM bundle pdfjs-dist@5 ships.
          opts.workerSrc = new URL(
            'pdfjs-dist/build/pdf.worker.min.mjs',
            import.meta.url,
          ).toString();
        }

        // Always feed pdfjs an ArrayBuffer. For Firebase Storage URLs we use
        // the Storage SDK's getBlob() instead of fetch() — that goes through
        // the SDK's authenticated transport which doesn't need a CORS preflight
        // (browser fetch() against the default Blaze bucket config DOES fail
        // CORS, which is what was breaking thumbnails). Non-Firebase URLs fall
        // back to direct fetch() since we have no SDK to use.
        let docData: ArrayBuffer;
        if (typeof source === 'string') {
          const isFirebaseUrl =
            source.includes('firebasestorage.googleapis.com') ||
            source.includes('.firebasestorage.app/');
          try {
            if (isFirebaseUrl) {
              const fileRef = storageRef(storage, source);
              const blob = await getBlob(fileRef);
              docData = await blob.arrayBuffer();
            } else {
              const resp = await fetch(source);
              if (!resp.ok) {
                throw new Error(`Drawing fetch failed: ${resp.status}`);
              }
              docData = await resp.arrayBuffer();
            }
          } catch (err) {
            const msg = (err as Error).message ?? String(err);
            throw new Error(
              msg.includes('storage/')
                ? `Drawing not available — ${msg}`
                : 'Drawing not available — link may have expired or the file was removed.',
            );
          }
        } else {
          docData = await source.arrayBuffer();
        }

        const loadingTask = pdfjs.getDocument({ data: docData });
        const loaded = (await loadingTask.promise) as unknown as PdfDocLike;
        if (cancelled) {
          await loaded.destroy();
          return;
        }
        setDoc(loaded);
      } catch (e) {
        if (!cancelled) setError(e as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, source]);

  // Destroy the pdfjs doc + revoke the blob URL on source/open change or
  // unmount. Split from the loader effect so we can cleanly tear down the
  // previous doc before a new one takes over.
  useEffect(() => {
    return () => {
      if (doc) {
        void doc.destroy().catch(() => {
          /* doc may already be gone — silent */
        });
      }
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
    };
  }, [doc]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [source, open]);

  // ────────────────────────────────────────────────────────────
  // Render current page → canvas whenever page/zoom/doc changes.
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!doc || !canvasRef.current) return;

    let cancelled = false;
    const canvas = canvasRef.current;
    const scale = ZOOM_STEPS[zoomIndex] ?? 1;

    (async () => {
      try {
        const page = await doc.getPage(pageNum);
        if (cancelled) return;

        const viewport = page.getViewport({ scale });
        // Match the canvas backing-store to viewport size. We don't bother
        // with devicePixelRatio scaling here — the zoom controls already
        // give the user control over crispness vs. screen footprint, and
        // doubling pixel count on a 4K screen makes the render painfully
        // slow for large drawings.
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Cancel any prior in-flight render so we don't paint a stale page
        // on top of the new one.
        renderTaskRef.current?.cancel();
        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        if (cancelled) return;
        renderTaskRef.current = null;
        page.cleanup?.();
      } catch (e) {
        // Render cancellations throw a RenderingCancelledException — those
        // are expected on fast page-flips and shouldn't surface as errors.
        const msg = (e as Error)?.message ?? '';
        if (cancelled || /cancel/i.test(msg)) return;
        setError(e as Error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [doc, pageNum, zoomIndex]);

  // ────────────────────────────────────────────────────────────
  // Keyboard controls.
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      // Ignore key events that originated from a focused input/textarea —
      // even though this modal doesn't render any, defensive against future
      // additions (e.g. a "go to page N" input).
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (!doc) return;
      if (e.key === 'ArrowRight') {
        setPageNum((p) => Math.min(doc.numPages, p + 1));
      } else if (e.key === 'ArrowLeft') {
        setPageNum((p) => Math.max(1, p - 1));
      } else if (e.key === '+' || e.key === '=') {
        setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1));
      } else if (e.key === '-' || e.key === '_') {
        setZoomIndex((i) => Math.max(0, i - 1));
      } else if (e.key === '0') {
        setZoomIndex(DEFAULT_ZOOM_INDEX);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, doc]);

  if (!open) return null;

  // ────────────────────────────────────────────────────────────
  // Download URL — for Blob sources we reuse the object URL created during
  // load; for string sources we hand the URL straight to the <a>. If the
  // source is null we still render the modal but disable the button.
  // ────────────────────────────────────────────────────────────
  const downloadUrl =
    blobUrlRef.current ?? (typeof source === 'string' ? source : null);
  const filename = downloadFilename || 'document.pdf';
  const zoomPct = Math.round((ZOOM_STEPS[zoomIndex] ?? 1) * 100);
  const canPrev = !!doc && pageNum > 1;
  const canNext = !!doc && pageNum < doc.numPages;
  const canZoomIn = zoomIndex < ZOOM_STEPS.length - 1;
  const canZoomOut = zoomIndex > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-viewer-title"
    >
      <div
        className="bg-paper rounded shadow-lg w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ───── Header ───── */}
        <div className="px-5 py-3 border-b border-line flex items-center justify-between bg-navy text-paper shrink-0 gap-3">
          <h2
            id="pdf-viewer-title"
            className="font-bold text-base truncate min-w-0 flex-1"
          >
            {title || 'PDF preview'}
          </h2>
          <div className="text-xs text-paper/70 font-mono shrink-0">
            {doc ? `Page ${pageNum} of ${doc.numPages}` : '—'}
          </div>
          {downloadUrl ? (
            <a
              href={downloadUrl}
              download={filename}
              className="text-xs bg-orange text-white font-semibold px-3 py-1.5 rounded hover:bg-orange/90 shrink-0"
            >
              ↓ Download
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="text-xs bg-orange/40 text-white font-semibold px-3 py-1.5 rounded shrink-0 cursor-not-allowed"
            >
              ↓ Download
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-paper/80 hover:text-paper text-lg leading-none shrink-0"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* ───── Main canvas area ───── */}
        <div className="flex-1 overflow-auto bg-bg/60 p-4 flex items-start justify-center">
          {source == null ? (
            <div className="text-sm text-text-mute italic py-16">
              No PDF loaded.
            </div>
          ) : error ? (
            <div className="max-w-md bg-orange/10 border-l-4 border-orange text-text px-4 py-3 text-sm self-center">
              <div className="font-semibold mb-1">⚠ Couldn't render PDF</div>
              <div className="text-xs text-text-mute mb-3 break-words">
                {error.message || 'Unknown error.'}
              </div>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  download={filename}
                  className="inline-block text-xs bg-orange text-white font-semibold px-3 py-1.5 rounded hover:bg-orange/90"
                >
                  ↓ Download to open externally
                </a>
              )}
            </div>
          ) : loading && !doc ? (
            // First-load skeleton — once the doc is parsed we let the
            // canvas itself absorb subsequent page-render latency so the
            // page indicator doesn't flicker on every nav.
            <div className="w-[600px] max-w-full h-[750px] bg-line animate-pulse rounded" />
          ) : (
            <canvas
              ref={canvasRef}
              className="bg-white shadow border border-line"
            />
          )}
        </div>

        {/* ───── Footer controls ───── */}
        <div className="px-5 py-2.5 border-t border-line flex items-center justify-between gap-3 bg-bg/50 shrink-0">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPageNum((p) => Math.max(1, p - 1))}
              disabled={!canPrev}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              ‹ Prev
            </button>
            <button
              type="button"
              onClick={() =>
                setPageNum((p) => (doc ? Math.min(doc.numPages, p + 1) : p))
              }
              disabled={!canNext}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              Next ›
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
              disabled={!canZoomOut}
              className="px-2.5 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed font-mono"
              aria-label="Zoom out"
            >
              −
            </button>
            <button
              type="button"
              onClick={() => setZoomIndex(DEFAULT_ZOOM_INDEX)}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg font-mono min-w-[64px]"
              aria-label="Reset zoom"
              title="Reset zoom"
            >
              {zoomPct}%
            </button>
            <button
              type="button"
              onClick={() =>
                setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))
              }
              disabled={!canZoomIn}
              className="px-2.5 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed font-mono"
              aria-label="Zoom in"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
